import { useToastProvider, useAnalytics, useCurrentUser } from '@qualio/ui-components';
import { AxiosError } from 'axios';
import { transformFormikToApiPayload } from 'lib';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Supplier, SupplierStatus } from 'types';
import { AddSupplierFormikPayload } from 'v2types';

import supplierApi from '../api/supplier.api';

type UseSupplierQueryType = {
  isSupplierLoading: boolean;
  supplier: Supplier | undefined;
  refetchSupplier: () => void;
  error?: AxiosError;
  isError: boolean;
};

export const useSupplierQuery = (supplierId?: string): UseSupplierQueryType => {
  const { companyId } = useCurrentUser();

  const {
    isLoading: isSupplierLoading,
    refetch: refetchSupplier,
    data: supplier,
    error,
    isError,
  } = useQuery(`supplierDetail-${supplierId}`, () => supplierApi.getSupplierById(companyId, supplierId), {
    enabled: !!supplierId,
    retry: false, // Don't retry on 404/403
  });

  return { isSupplierLoading, supplier, refetchSupplier, error: error as AxiosError, isError };
};

type UseSupplierArchiveMutationType = {
  isArchiveSupplierLoading: boolean;
  archiveSupplierMutate: (supplier: Supplier) => void;
};

export const useSupplierArchiveMutation = (onSuccess: () => void): UseSupplierArchiveMutationType => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { companyId } = useCurrentUser();

  const { isLoading: isArchiveSupplierLoading, mutate: archiveSupplierMutate } = useMutation(
    (supplier: Supplier) => supplierApi.archive(companyId, supplier.supplier as string),
    {
      onSuccess: (data) => {
        showToast({
          title: t('toastMessages.supplier.archived'),
          description: t('toastMessages.supplier.archivedDescription', { name: data.name }),
          status: 'success',
          'data-cy': 'toast-supplier-archived',
        });
        onSuccess();
      },
      onError: () => {
        showToast({
          title: t('toastMessages.supplier.failure'),
          description: t('toastMessages.supplier.notArchived'),
          status: 'error',
        });
      },
    },
  );
  return { isArchiveSupplierLoading, archiveSupplierMutate };
};

type UseSupplierUnArchiveMutationType = {
  isUnArchiveSupplierLoading: boolean;
  unArchiveSupplierMutate: (supplier: Supplier) => void;
};

export const useSupplierUnArchiveMutation = (onSuccess: () => void): UseSupplierUnArchiveMutationType => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { companyId } = useCurrentUser();

  const { isLoading: isUnArchiveSupplierLoading, mutate: unArchiveSupplierMutate } = useMutation(
    (supplier: Supplier) => supplierApi.createSupplier(companyId, { ...supplier, status: 'DRAFT' }),
    {
      onSuccess: ({ supplier }) => {
        showToast({
          title: t('toastMessages.supplier.unArchived'),
          description: t('toastMessages.supplier.unArchivedDescription', { name: supplier.name }),
          status: 'success',
          'data-cy': 'toast-supplier-unarchived',
        });
        onSuccess();
      },
      onError: () => {
        showToast({
          title: t('toastMessages.supplier.failure'),
          description: t('toastMessages.supplier.notUnArchived'),
          status: 'error',
        });
      },
    },
  );

  return { isUnArchiveSupplierLoading, unArchiveSupplierMutate };
};

type RevertToDraftSupplierType = {
  isRevertToDraftSupplierLoading: boolean;
  revertToDraftSupplierMutate: (supplier: Supplier) => void;
};

export const useSupplierRevertToDraftMutation = (onSuccess: () => void): RevertToDraftSupplierType => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { companyId } = useCurrentUser();

  const { isLoading: isRevertToDraftSupplierLoading, mutate: revertToDraftSupplierMutate } = useMutation(
    (supplier: Supplier) => supplierApi.createSupplier(companyId, { ...supplier, status: 'DRAFT' }),
    {
      onSuccess: ({ supplier }) => {
        showToast({
          title: t('toastMessages.supplier.reverted'),
          description: t('toastMessages.supplier.revertedToDraftDescription', { name: supplier.name }),
          status: 'success',
        });
        onSuccess();
      },
      onError: () => {
        showToast({
          title: t('toastMessages.supplier.failure'),
          description: t('toastMessages.supplier.notRevertedToDraft'),
          status: 'error',
        });
      },
    },
  );

  return { isRevertToDraftSupplierLoading, revertToDraftSupplierMutate };
};

type MutateSupplier = { supplier: AddSupplierFormikPayload; status: SupplierStatus; isDraft: boolean };

export type SupplierMutationFailureStep = 'createSupplier' | 'sendForReview';

export type SupplierMutationError = AxiosError<{ errors: { field: string; message: string[] }[] }> & {
  failureStep?: SupplierMutationFailureStep;
};

type MutateOptions = {
  onError?: (args: SupplierMutationError) => void;
};

type UseCreateSupplierMutate = {
  createSupplierMutate: (data: MutateSupplier, options?: MutateOptions) => void;
  isSupplierCreating: boolean;
};

export const useCreateSupplierMutate = (onSuccess: (supplierId: string) => void): UseCreateSupplierMutate => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { companyId } = useCurrentUser();

  const { isLoading: isSupplierCreating, mutate: createSupplierMutate } = useMutation(
    ({
      supplier,
      status,
      isDraft,
    }: {
      supplier: AddSupplierFormikPayload;
      status: SupplierStatus;
      isDraft: boolean;
    }) => {
      if (!isDraft) {
        return supplierApi
          .createSupplierV2(companyId, { ...transformFormikToApiPayload(supplier), status })
          .catch((error: SupplierMutationError) => {
            error.failureStep = 'createSupplier';
            throw error;
          })
          .then((supplierResponse) =>
            supplierApi
              .sendForReview(companyId, supplierResponse.supplier.supplier as string)
              .catch((error: SupplierMutationError) => {
                error.failureStep = 'sendForReview';
                throw error;
              })
              .then(() => ({ ...supplierResponse, isDraft: false })),
          );
      } else {
        return supplierApi
          .createSupplierV2(companyId, { ...transformFormikToApiPayload(supplier), status })
          .then((response) => ({ ...response, isDraft: status === 'DRAFT' }));
      }
    },
    {
      onSuccess: ({ supplier, isDraft }) => {
        if (!isDraft) {
          showToast({
            title: t('toastMessages.supplier.saved'),
            description: t('toastMessages.supplier.savedAsDraftDescription', { name: supplier.name }),
            status: 'success',
            'data-cy': 'toast-supplier-saved',
          });
          showToast({
            title: t('toastMessages.supplier.sent'),
            description: t('toastMessages.supplier.sentForApprovalDescription', { name: supplier.name }),
            status: 'success',
            'data-cy': 'toast-supplier-sent-for-approval',
          });
        } else if (supplier.status === 'REVIEW_PENDING') {
          showToast({
            title: t('toastMessages.supplier.sent'),
            description: t('toastMessages.supplier.sentForApprovalDescription', { name: supplier.name }),
            status: 'success',
            'data-cy': 'toast-supplier-sent-for-approval',
          });
        } else {
          showToast({
            title: t('toastMessages.supplier.saved'),
            description: t('toastMessages.supplier.savedAsDraftDescription', { name: supplier.name }),
            status: 'success',
            'data-cy': 'toast-supplier-saved',
          });
        }
        onSuccess(supplier.supplier!);
      },
      onError: (error: SupplierMutationError) => {
        const description =
          error.failureStep === 'sendForReview'
            ? t('toastMessages.supplier.notSentForApproval')
            : t('toastMessages.supplier.notSavedAsDraft');
        showToast({
          title: t('toastMessages.supplier.failure'),
          description,
          status: 'error',
        });
      },
    },
  );
  return { isSupplierCreating, createSupplierMutate } as UseCreateSupplierMutate;
};

type UseSupplierExportMutationType = {
  isExportSupplierLoading: boolean;
  exportSupplierMutate: (supplier: Supplier) => void;
};

export const useSupplierExportMutation = (onSuccess: () => void): UseSupplierExportMutationType => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { companyId } = useCurrentUser();

  const { isLoading: isExportSupplierLoading, mutate: exportSupplierMutate } = useMutation(
    (supplier: Supplier) => supplierApi.export(companyId, supplier.supplier as string),
    {
      onSuccess: (data) => {
        showToast({
          title: t('toastMessages.supplier.fileReady'),
          description: (
            <span>
              {t('toastMessages.supplier.fileReadyDescription')}{' '}
              <a style={{ color: '#0069FF' }} href={data.url}>
                {t('toastMessages.supplier.download')}
              </a>
            </span>
          ),
          status: 'success',
          'data-cy': 'toast-supplier-file-ready',
        });
        onSuccess();
      },
      onError: () => {
        showToast({
          title: t('toastMessages.supplier.failure'),
          description: t('toastMessages.supplier.notExported'),
          status: 'error',
        });
      },
    },
  );
  return { isExportSupplierLoading, exportSupplierMutate };
};

type RevertSupplierToDraftMutationType = {
  isRevertSupplierToDraftLoading: boolean;
  revertSupplierToDraftMutate: (supplier: Supplier) => void;
};

export const useRevertSupplierToDraftMutationType = (onSuccess: () => void): RevertSupplierToDraftMutationType => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { companyId } = useCurrentUser();

  const { isLoading: isRevertSupplierToDraftLoading, mutate: revertSupplierToDraftMutate } = useMutation(
    (supplier: Supplier) => supplierApi.revertToDraft(companyId, supplier.supplier as string),
    {
      onSuccess: (supplier) => {
        if (supplier.status === 'DRAFT') {
          showToast({
            title: t('toastMessages.supplier.sent'),
            description: t('toastMessages.supplier.revertedToDraftDescription', { name: supplier.name }),
            status: 'success',
          });
        }
        onSuccess();
      },
      onError: () => {
        showToast({
          title: t('toastMessages.supplier.failure'),
          description: t('toastMessages.supplier.notRevertedToDraft'),
          status: 'error',
        });
      },
    },
  );
  return { isRevertSupplierToDraftLoading, revertSupplierToDraftMutate };
};

type UseSupplierSpreadsheetExportMutationType = {
  isExportSupplierSpreadsheetLoading: boolean;
  exportSupplierSpreadsheetMutate: (supplierIds: string[]) => void;
};

export const useSupplierSpreadsheetExportMutation = (): UseSupplierSpreadsheetExportMutationType => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const analytics = useAnalytics();
  const { companyId } = useCurrentUser();

  const { isLoading: isExportSupplierSpreadsheetLoading, mutate: exportSupplierSpreadsheetMutate } = useMutation(
    (supplierIds: string[]) => supplierApi.exportSpreadsheet(companyId, supplierIds),
    {
      onMutate: () => {
        showToast({
          title: t('toastMessages.supplier.preparingFile'),
          description: t('toastMessages.supplier.preparingFileDescription'),
          status: 'info',
          duration: 10000,
        });
        analytics.track('Suppliers | Export Supplier List as Spreadsheet');
      },
      onSuccess: (data) => {
        showToast({
          title: t('toastMessages.supplier.fileReady'),
          description: (
            <span>
              {t('toastMessages.supplier.fileReadyDescription')}{' '}
              <a style={{ color: '#0069FF' }} href={data.url}>
                {t('toastMessages.supplier.download')}
              </a>
            </span>
          ),
          status: 'success',
          duration: null, //https://chakra-ui.com/docs/components/toast/props#:~:text=If%20set%20to%20null%2C%20toast%20will%20never%20dismiss.
          'data-cy': 'toast-supplier-file-ready',
        });
      },
      onError: () => {
        showToast({
          title: t('toastMessages.supplier.failure'),
          description: t('toastMessages.supplier.suppliersNotExported'),
          status: 'error',
        });
      },
    },
  );
  return { isExportSupplierSpreadsheetLoading, exportSupplierSpreadsheetMutate };
};
